/**
 * AnimationRetargeting - runtime animation retargeting between different skeleton conventions.
 * Uses the UPF GTI algorithm with quaternion space transformations to handle bind pose differences.
 * Enables animations from external sources (Mixamo, UE5, etc.) on characters with different bone naming.
 */
import { applyTPose as applyTPoseInternal, AnimationRetargeting as UPFRetargeting } from './retargeting.js';
// Re-export for external use
export { applyTPoseInternal as applyTPose };
import { BoneRole } from './SkeletonProfile.js';

import type { SkeletonProfile } from './SkeletonProfile.js';
import type * as THREE from 'three';

/** Default name for synthetic root bones */
const SYNTHETIC_ROOT_BONE_NAME = 'syntheticRoot';

/**
 * Result of adding a synthetic root bone to a skeleton.
 */
export interface SyntheticRootResult {
  /** The modified skeleton with synthetic root */
  skeleton: THREE.Skeleton;
  /** Name of the synthetic root bone */
  rootBoneName: string;
  /** Updated profile with synthetic root mapping */
  updatedProfile: SkeletonProfile;
}

/**
 * Synthetic root injection is intentionally unsupported at runtime.
 *
 * Runtime topology mutation (adding bones) can invalidate preallocated skinning
 * buffers/bindings, especially on WebGPU. Keep target skeleton topology fixed.
 */
export function addSyntheticRootBone(
  skeleton: THREE.Skeleton,
  _skinnedMesh: THREE.SkinnedMesh,
  profile: SkeletonProfile,
  _rootBoneName: string = SYNTHETIC_ROOT_BONE_NAME
): SyntheticRootResult {
  const existingRootBone = Object.entries(profile.boneToRole)
    .find(([, role]) => role === BoneRole.Root)?.[0];
  if (existingRootBone) {
    return { skeleton, rootBoneName: existingRootBone, updatedProfile: profile };
  }
  throw new Error(
    '[AnimationRetargeting] Synthetic root injection is not supported. ' +
    'Retarget root-motion clips only to targets that already include a real root bone.'
  );
}

export function getDefaultRetargeter(
  boneMapping: Record<string, string>,
  sourceSkeleton: THREE.Skeleton,
  targetSkeleton: THREE.Skeleton,
  sourceProfile: SkeletonProfile,
  targetProfile: SkeletonProfile
): UPFRetargeting {
  return new UPFRetargeting(sourceSkeleton, targetSkeleton, {
    boneNameMap: boneMapping,
    trgPoseMode: UPFRetargeting.BindPoseModes.CURRENT,
    trgEmbedWorldTransforms: true,
    srcEmbedWorldTransforms: true,
    srcPoseMode: UPFRetargeting.BindPoseModes.CURRENT,
    srcHipsBoneName: sourceProfile.hipBoneName,
    positionAxisFlips: targetProfile.positionAxisFlips,
    hipsPositionAxisFlips: targetProfile.hipsPositionAxisFlips
  });
}

/**
 * Check if a skeleton needs a synthetic root bone for retargeting from a source that has one.
 */
export function needsSyntheticRoot(
  _sourceProfile: SkeletonProfile,
  _targetProfile: SkeletonProfile
): boolean {
  return false;
}

/**
 * Add getBoneByName helper to skeleton if it doesn't exist
 */
function ensureGetBoneByName(skeleton: THREE.Skeleton): void {
  if (!(skeleton as any).getBoneByName) {
    (skeleton as any).getBoneByName = function(name: string) {
      return this.bones.find((b: THREE.Bone) => b.name === name);
    };
  }
}

/**
 * Convert SkeletonProfile to the format applyTPose expects
 */
function profileToTPoseMap(profile: SkeletonProfile): Record<string, string | undefined> {
  // Invert boneToRole to get roleToName
  const roleToName: Partial<Record<BoneRole, string>> = {};
  for (const [boneName, role] of Object.entries(profile.boneToRole)) {
    roleToName[role] = boneName;
  }

  // Map to the semantic keys applyTPose expects
  return {
    Hips: roleToName[BoneRole.Hips],
    Stomach: roleToName[BoneRole.Spine],
    ShouldersUnion: roleToName[BoneRole.Chest] ?? roleToName[BoneRole.UpperChest],
    Neck: roleToName[BoneRole.Neck],
    Head: roleToName[BoneRole.Head],
    LShoulder: roleToName[BoneRole.LeftShoulder],
    LArm: roleToName[BoneRole.LeftUpperArm],
    LElbow: roleToName[BoneRole.LeftLowerArm],
    LWrist: roleToName[BoneRole.LeftHand],
    RShoulder: roleToName[BoneRole.RightShoulder],
    RArm: roleToName[BoneRole.RightUpperArm],
    RElbow: roleToName[BoneRole.RightLowerArm],
    RWrist: roleToName[BoneRole.RightHand],
    LUpLeg: roleToName[BoneRole.LeftUpperLeg],
    LLeg: roleToName[BoneRole.LeftLowerLeg],
    LFoot: roleToName[BoneRole.LeftFoot],
    LToeBase: roleToName[BoneRole.LeftToeBase],
    RUpLeg: roleToName[BoneRole.RightUpperLeg],
    RLeg: roleToName[BoneRole.RightLowerLeg],
    RFoot: roleToName[BoneRole.RightFoot],
    RToeBase: roleToName[BoneRole.RightToeBase]
  };
}

/**
 * Create a bone name mapping between source and target skeletons using their profiles.
 * Returns an object compatible with UPF AnimationRetargeting: { sourceRole: targetBoneName }
 */
export function createBoneMapping(
  sourceProfile: SkeletonProfile,
  targetProfile: SkeletonProfile,
  sourceSkeleton: THREE.Skeleton,
  targetSkeleton: THREE.Skeleton
): Record<string, string> {
  const mapping: Record<string, string> = {};

  // Build reverse map for source: role -> bone name
  const sourceRoleToBone = new Map<BoneRole, string>();
  for (const bone of sourceSkeleton.bones) {
    const role = sourceProfile.boneToRole[bone.name];
    if (role) {
      sourceRoleToBone.set(role, bone.name);
    }
  }

  // Build reverse map for target: role -> bone name
  const targetRoleToBone = new Map<BoneRole, string>();
  for (const bone of targetSkeleton.bones) {
    const role = targetProfile.boneToRole[bone.name];
    if (role) {
      targetRoleToBone.set(role, bone.name);
    }
  }

  // Map source bones to target bones via semantic roles
  for (const [role, sourceBoneName] of sourceRoleToBone) {
    const targetBoneName = targetRoleToBone.get(role);
    if (targetBoneName) {
      mapping[sourceBoneName] = targetBoneName;
    }
  }

  // Note: Root motion is dropped when target has no root bone

  return mapping;
}

/**
 * Retarget an animation clip from source skeleton to target skeleton using UPF GTI retargeting.
 *
 * @param sourceClip - The animation clip to retarget
 * @param sourceProfile - Skeleton profile for the source animation
 * @param targetProfile - Skeleton profile for the target character
 * @param sourceSkeleton - Source skeleton (from the animation's original GLTF)
 * @param targetSkeleton - Target skeleton (from the character being animated)
 * @param targetSkinnedMesh - Target skinned mesh (required by Three.js retargeting)
 * @returns Retargeted animation clip, or null if retargeting failed
 */
export function retargetAnimationClip(
  sourceClip: THREE.AnimationClip,
  sourceProfile: SkeletonProfile,
  targetProfile: SkeletonProfile,
  sourceSkeleton: THREE.Skeleton,
  targetSkeleton: THREE.Skeleton,
  targetSkinnedMesh: THREE.SkinnedMesh
): THREE.AnimationClip | null {
  // Build reverse map for source: role -> bone name
  const sourceRoleToBone = new Map<BoneRole, string>();
  for (const bone of sourceSkeleton.bones) {
    const role = sourceProfile.boneToRole[bone.name];
    if (role) {
      sourceRoleToBone.set(role, bone.name);
    }
  }

  // Create bone mapping using our profile system
  const boneMapping = createBoneMapping(
    sourceProfile,
    targetProfile,
    sourceSkeleton,
    targetSkeleton
  );

  if (Object.keys(boneMapping).length === 0) {
    console.error('[AnimationRetargeting] Failed to create bone mapping - no bones matched');
    return null;
  }

  try {
    // Add getBoneByName helper method required by applyTPose
    ensureGetBoneByName(sourceSkeleton);
    ensureGetBoneByName(targetSkeleton);

    // Convert our profiles to the format applyTPose expects
    const sourceTPoseMap = profileToTPoseMap(sourceProfile);
    const targetTPoseMap = profileToTPoseMap(targetProfile);

    // Determine if T-Pose should be applied based on profile flags
    const shouldTPoseSource = sourceProfile.forceTPose === true;
    const shouldTPoseTarget = targetProfile.forceTPose === true;

    // Apply T-Pose conditionally based on profile flags
    try {
      if (shouldTPoseSource) {
        applyTPoseInternal(sourceSkeleton, sourceTPoseMap);
        console.debug('[AnimationRetargeting] Applied T-Pose to source skeleton');
      }
      if (shouldTPoseTarget) {
        applyTPoseInternal(targetSkeleton, targetTPoseMap);
        console.debug('[AnimationRetargeting] Applied T-Pose to target skeleton');
      }

      // Create UPF retargeting instance using CURRENT pose mode to capture the T-Poses
      const retargeter = getDefaultRetargeter(boneMapping, sourceSkeleton, targetSkeleton, sourceProfile, targetProfile);

      // Retarget the animation
      const retargetedClip = retargeter.retargetAnimation(sourceClip);

      if (!retargetedClip || retargetedClip.tracks.length === 0) {
        console.error(`[AnimationRetargeting] Failed to retarget "${sourceClip.name}": no tracks generated`);
        return null;
      }

      // CRITICAL: Remove scale tracks to prevent shrinking/deformation
      const originalTrackCount = retargetedClip.tracks.length;
      retargetedClip.tracks = retargetedClip.tracks.filter(track => {
        return !track.name.endsWith('.scale');
      });

      // Handle root motion incompatibility between source and target
      const sourceRootBoneName = sourceRoleToBone.get(BoneRole.Root);
      const targetRootBoneName = targetProfile.boneToRole[targetSkeleton.bones[0]?.name];
      const targetHipsBoneName = Array.from(Object.entries(targetProfile.boneToRole))
        .find(([, role]) => role === BoneRole.Hips)?.[0];

      // Case 1: Source has no root bone but target does
      // Mixamo hips position tracks should NOT be applied to Rigify hips (child of root)
      if (!sourceRootBoneName && targetRootBoneName === BoneRole.Root && targetHipsBoneName) {
        const hipsPositionTrackIndex = retargetedClip.tracks.findIndex(track =>
          track.name.includes(targetHipsBoneName) && track.name.endsWith('.position')
        );

        if (hipsPositionTrackIndex !== -1) {
          retargetedClip.tracks.splice(hipsPositionTrackIndex, 1);
          console.warn(
            `[AnimationRetargeting] Removed hips position track from "${sourceClip.name}" ` +
            'because source skeleton lacks a root bone (Mixamo) but target has one (Rigify).'
          );
        }
      }

      // Case 2: Source has root motion but target doesn't
      const sourceHasRootMotion = sourceRootBoneName &&
        sourceClip.tracks.some(track =>
          track.name.includes(sourceRootBoneName) && track.name.endsWith('.position')
        );

      if (sourceHasRootMotion && targetRootBoneName !== BoneRole.Root) {
        console.warn(
          `[AnimationRetargeting] Source animation "${sourceClip.name}" contains root motion. ` +
          'Root motion tracks are not transferred when target skeleton lacks a root bone. ' +
          'Synthetic root injection is disabled; use a target rig that includes a real root bone.'
        );
      }

      // Reset all bone scales to (1,1,1) to prevent accumulated scale issues
      targetSkeleton.bones.forEach(bone => {
        bone.scale.set(1, 1, 1);
      });
      targetSkeleton.update();

      return retargetedClip;

    } catch (tPoseError) {
      console.error('[AnimationRetargeting] T-Pose failed, falling back to default bind poses:', tPoseError);

      // Fallback: use the same retargeter options as the primary path.
      const retargeter = getDefaultRetargeter(boneMapping, sourceSkeleton, targetSkeleton, sourceProfile, targetProfile);

      // Retarget the animation
      const retargetedClip = retargeter.retargetAnimation(sourceClip);

      if (!retargetedClip || retargetedClip.tracks.length === 0) {
        console.error(`[AnimationRetargeting] Failed to retarget "${sourceClip.name}": no tracks generated`);
        return null;
      }

      // CRITICAL: Remove scale tracks to prevent shrinking/deformation
      retargetedClip.tracks = retargetedClip.tracks.filter(track => {
        return !track.name.endsWith('.scale');
      });

      targetSkeleton.bones.forEach(bone => {
        bone.scale.set(1, 1, 1);
      });
      targetSkeleton.update();

      return retargetedClip;
    }

  } catch (error) {
    console.error(`[AnimationRetargeting] Failed to retarget "${sourceClip.name}":`, error);
    return null;
  }
}

/**
 * A reusable retargeting session that initializes T-poses once and can retarget multiple clips.
 * This is more efficient than calling retargetAnimationClip for each clip individually.
 */
export class RetargetingSession {
  private retargeter: InstanceType<typeof UPFRetargeting> | null = null;
  private sourceRoleToBone = new Map<BoneRole, string>();
  private targetProfile: SkeletonProfile;
  private targetSkeleton: THREE.Skeleton;
  private initialized = false;

  constructor(
    sourceProfile: SkeletonProfile,
    targetProfile: SkeletonProfile,
    sourceSkeleton: THREE.Skeleton,
    targetSkeleton: THREE.Skeleton
  ) {
    this.targetProfile = targetProfile;
    this.targetSkeleton = targetSkeleton;

    // Build reverse map for source: role -> bone name
    for (const bone of sourceSkeleton.bones) {
      const role = sourceProfile.boneToRole[bone.name];
      if (role) {
        this.sourceRoleToBone.set(role, bone.name);
      }
    }

    // Create bone mapping
    const boneMapping = createBoneMapping(sourceProfile, targetProfile, sourceSkeleton, targetSkeleton);

    if (Object.keys(boneMapping).length === 0) {
      console.error('[RetargetingSession] Failed to create bone mapping - no bones matched');
      return;
    }

    // Add getBoneByName helper
    ensureGetBoneByName(sourceSkeleton);
    ensureGetBoneByName(targetSkeleton);

    // Convert profiles to T-pose map format
    const sourceTPoseMap = profileToTPoseMap(sourceProfile);
    const targetTPoseMap = profileToTPoseMap(targetProfile);

    // Determine if T-Pose should be applied based on profile flags
    const shouldTPoseSource = sourceProfile.forceTPose === true;
    const shouldTPoseTarget = targetProfile.forceTPose === true;

    // Apply T-Pose ONCE to skeletons conditionally
    try {
      if (shouldTPoseSource) {
        applyTPoseInternal(sourceSkeleton, sourceTPoseMap);
        console.debug('[RetargetingSession] Applied T-Pose to source skeleton');
      }
      if (shouldTPoseTarget) {
        applyTPoseInternal(targetSkeleton, targetTPoseMap);
        console.debug('[RetargetingSession] Applied T-Pose to target skeleton');
      }

      // Create retargeter instance ONCE
      this.retargeter = getDefaultRetargeter(boneMapping, sourceSkeleton, targetSkeleton, sourceProfile, targetProfile);

      this.initialized = true;
      console.debug('[RetargetingSession] Initialized with T-poses applied once');
    } catch (error) {
      console.error('[RetargetingSession] Failed to initialize:', error);
    }
  }

  /**
   * Retarget a single clip using the pre-initialized session.
   */
  retargetClip(sourceClip: THREE.AnimationClip): THREE.AnimationClip | null {
    if (!this.initialized || !this.retargeter) {
      console.error('[RetargetingSession] Session not initialized');
      return null;
    }

    try {
      const retargetedClip = this.retargeter.retargetAnimation(sourceClip);

      if (!retargetedClip || retargetedClip.tracks.length === 0) {
        console.error(`[RetargetingSession] Failed to retarget "${sourceClip.name}": no tracks generated`);
        return null;
      }

      // Remove scale tracks
      retargetedClip.tracks = retargetedClip.tracks.filter(track => !track.name.endsWith('.scale'));

      // Handle root motion incompatibility
      const sourceRootBoneName = this.sourceRoleToBone.get(BoneRole.Root);
      const targetRootBoneName = this.targetProfile.boneToRole[this.targetSkeleton.bones[0]?.name];
      const targetHipsBoneName = Array.from(Object.entries(this.targetProfile.boneToRole))
        .find(([, role]) => role === BoneRole.Hips)?.[0];

      if (!sourceRootBoneName && targetRootBoneName === BoneRole.Root && targetHipsBoneName) {
        const hipsPositionTrackIndex = retargetedClip.tracks.findIndex(track =>
          track.name.includes(targetHipsBoneName) && track.name.endsWith('.position')
        );
        if (hipsPositionTrackIndex !== -1) {
          retargetedClip.tracks.splice(hipsPositionTrackIndex, 1);
        }
      }

      const sourceHasRootMotion = sourceRootBoneName &&
        sourceClip.tracks.some(track =>
          track.name.includes(sourceRootBoneName) && track.name.endsWith('.position')
        );
      if (sourceHasRootMotion && targetRootBoneName !== BoneRole.Root) {
        console.warn(
          `[RetargetingSession] Source animation "${sourceClip.name}" contains root motion. ` +
          'Root motion tracks are not transferred when target skeleton lacks a root bone. ' +
          'Synthetic root injection is disabled; use a target rig that includes a real root bone.'
        );
      }

      return retargetedClip;
    } catch (error) {
      console.error(`[RetargetingSession] Failed to retarget "${sourceClip.name}":`, error);
      return null;
    }
  }

  /**
   * Retarget multiple clips using the pre-initialized session.
   */
  retargetClips(sourceClips: THREE.AnimationClip[]): Array<{ clip: THREE.AnimationClip | null; success: boolean; originalName: string }> {
    return sourceClips.map(sourceClip => ({
      clip: this.retargetClip(sourceClip),
      success: this.retargetClip(sourceClip) !== null,
      originalName: sourceClip.name
    }));
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}

/**
 * Batch retarget multiple animation clips.
 * Uses RetargetingSession internally to apply T-poses only once.
 */
export function retargetAnimationClips(
  sourceClips: THREE.AnimationClip[],
  sourceProfile: SkeletonProfile,
  targetProfile: SkeletonProfile,
  sourceSkeleton: THREE.Skeleton,
  targetSkeleton: THREE.Skeleton,
  _targetSkinnedMesh: THREE.SkinnedMesh
): Array<{ clip: THREE.AnimationClip | null; success: boolean; originalName: string }> {
  // Create a session that initializes T-poses once
  const session = new RetargetingSession(sourceProfile, targetProfile, sourceSkeleton, targetSkeleton);

  if (!session.isInitialized()) {
    return sourceClips.map(clip => ({ clip: null, success: false, originalName: clip.name }));
  }

  // Retarget all clips using the same session
  return sourceClips.map(sourceClip => {
    const retargetedClip = session.retargetClip(sourceClip);
    return {
      clip: retargetedClip,
      success: retargetedClip !== null,
      originalName: sourceClip.name
    };
  });
}

